/*
  DocumentViewer – self-contained multi-format viewer (PDF, DOCX, Markdown, HTML)

  The component lazy-loads heavy rendering libraries so that the base bundle
  stays small.  All rendering is done client-side; no external embeds are used
  (important for locked-down school environments).

  Supported file types:
   • pdf      → rendered via Mozilla pdf.js (pdfjs-dist)
   • docx     → converted to HTML via mammoth
   • markdown → rendered with markdown-it → sanitised with DOMPurify
   • html     → fetched & sanitised with DOMPurify

  Highlights/annotations:  For now the viewer emits regular HTML spans (text
  layer for PDF) so our existing selection-wrapper logic in MarkingViewPage
  continues to work unchanged.

  Limitations / TODOs
   – paginated PDF scroll performance can be improved using virtualisation.
   – .doc (legacy) files still need backend conversion.
*/

import * as React from "react";
import { useEffect, useRef, useState } from "react";

export type SupportedDocumentType = "pdf" | "docx" | "markdown" | "html";

interface DocumentViewerProps {
  url: string;
  type?: SupportedDocumentType;
  className?: string;
}

/* ────────────────────────────────────────────────── PDF ─────────────── */

function PdfViewer({ url, className }: { url: string; className: string }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let destroyed = false;

    (async () => {
      try {
        // Dynamic import keeps pdfjs out of main bundle
        const pdfjsLib = await import("pdfjs-dist" /* webpackChunkName: "pdfjs" */);
        // pdf.js needs to know where its worker script lives.  Appending
        // `?url` makes Vite give us the public URL string to that file – this
        // works **identically** in dev (served from the dev server) and after
        // a production build (hashed asset copied to /dist).  No more CDN or
        // path‐guessing.

        const { default: workerSrc } = await import(
          /* @vite-ignore */
          "pdfjs-dist/build/pdf.worker.min.mjs?url"
        );

        // @ts-ignore – upstream types are missing GlobalWorkerOptions
        pdfjsLib.GlobalWorkerOptions.workerSrc = workerSrc as string;

        const loadingTask = pdfjsLib.getDocument(url);
        const pdf = await loadingTask.promise;

        const scale = 1.2;

        for (let pageNum = 1; pageNum <= pdf.numPages && !destroyed; pageNum++) {
          const page = await pdf.getPage(pageNum);
          const viewport = page.getViewport({ scale });

          // Canvas for raster layer
          const canvas = document.createElement("canvas");
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          canvas.className = "mb-4 block";
          const ctx = canvas.getContext("2d")!;

          // Container for both canvas & text layer (positioned absolute)
          const wrapper = document.createElement("div");
          wrapper.className = "relative";
          wrapper.appendChild(canvas);

          containerRef.current?.appendChild(wrapper);

          await page.render({ canvasContext: ctx, viewport }).promise;

          // Text layer so selection works
          const textContent = await page.getTextContent();
          const textLayerDiv = document.createElement("div");
          textLayerDiv.className = "absolute inset-0 textLayer";
          wrapper.appendChild(textLayerDiv);

          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore – pdfjs types are incomplete for renderTextLayer
          pdfjsLib.renderTextLayer({
            container: textLayerDiv,
            textContent,
            viewport,
            enhanceTextSelection: true,
          });
        }
      } catch (err) {
        console.error(err);
        setError((err as Error).message);
      }
    })();

    return () => {
      destroyed = true;
      if (containerRef.current) containerRef.current.innerHTML = "";
    };
  }, [url]);

  if (error) {
    return (
      <p className="p-4 text-red-600">Failed to load PDF: {error}</p>
    );
  }

  return <div ref={containerRef} className={`${className} overflow-auto`} />;
}

/* ─────────────────────────────────────────────── DOCX ──────────────── */

function DocxViewer({ url, className }: { url: string; className: string }) {
  const [html, setHtml] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [{ convertToHtml }, dompurifyMod] = await Promise.all([
          import("mammoth" /* webpackChunkName: "mammoth" */),
          import("dompurify" /* webpackChunkName: "dompurify" */),
        ]);
        // @ts-ignore – CJS/ESM interop
        const DOMPurify = (dompurifyMod.default || dompurifyMod) as typeof import("dompurify");

        const res = await fetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const arrayBuffer = await res.arrayBuffer();

        const { value } = await convertToHtml({ arrayBuffer });
        const clean = DOMPurify.sanitize(value, { WHOLE_DOCUMENT: false });
        if (!cancelled) setHtml(clean);
      } catch (err) {
        console.error(err);
        if (!cancelled) setError((err as Error).message);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [url]);

  if (error) return <p className="p-4 text-red-600">Failed: {error}</p>;
  if (!html) return <p className="p-4 text-gray-500">Loading…</p>;

  return <div className={`${className} overflow-auto prose dark:prose-invert p-4`} dangerouslySetInnerHTML={{ __html: html }} />;
}

/* ─────────────────────────────── Markdown & HTML ──────────────────── */

function MdOrHtmlViewer({ url, markdown, className }: { url: string; markdown: boolean; className: string }) {
  const [html, setHtml] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const dompurifyMod = await import("dompurify" /* webpackChunkName: "dompurify" */);
        // @ts-ignore
        const DOMPurify = (dompurifyMod.default || dompurifyMod) as typeof import("dompurify");
        const res = await fetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const text = await res.text();

        const htmlString = markdown
          ? (await (async () => {
              const MarkdownIt = (await import(
                "markdown-it" /* webpackChunkName: "markdown-it" */
              )).default;
              const md = new MarkdownIt({ linkify: true, html: false });
              return md.render(text);
            })())
          : text;

        const clean = DOMPurify.sanitize(htmlString, { WHOLE_DOCUMENT: false });
        if (!cancelled) setHtml(clean);
      } catch (err) {
        console.error(err);
        if (!cancelled) setError((err as Error).message);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [url, markdown]);

  if (error) return <p className="p-4 text-red-600">Failed: {error}</p>;
  if (!html) return <p className="p-4 text-gray-500">Loading…</p>;

  return <div className={`${className} overflow-auto prose dark:prose-invert p-4`} dangerouslySetInnerHTML={{ __html: html }} />;
}

/* ────────────────────────────────────────── MAIN WRAPPER ───────────── */

export default function DocumentViewer({ url, type: explicitType, className = "w-full h-full" }: DocumentViewerProps) {
  // Infer type from extension if not provided
  const type: SupportedDocumentType | undefined = React.useMemo(() => {
    if (explicitType) return explicitType;
    const m = /\.([a-zA-Z0-9]+)(?:[?#]|$)/.exec(url);
    if (!m) return undefined;
    const ext = m[1].toLowerCase();
    if (ext === "pdf") return "pdf";
    if (ext === "docx") return "docx";
    if (ext === "md" || ext === "markdown") return "markdown";
    if (ext === "htm" || ext === "html") return "html";
    return undefined;
  }, [explicitType, url]);

  if (!type) return <p className="text-red-600">Unknown/unsupported document type</p>;

  switch (type) {
    case "pdf":
      return <PdfViewer url={url} className={className} />;
    case "docx":
      return <DocxViewer url={url} className={className} />;
    case "markdown":
      return <MdOrHtmlViewer url={url} markdown className={className} />;
    case "html":
      return <MdOrHtmlViewer url={url} markdown={false} className={className} />;
    default:
      return <p className="text-red-600">Unsupported type: {type}</p>;
  }
}
